import type { Lang } from './i18n';

// Messages carry small markers written by the AI or the platform. They are turned into
// tappable elements here; everything else is rendered as escaped text with light formatting.

export interface ServiceOption { name: string; price: string; detail: string }
export interface BookingCard {
  code: string; service: string; slot: string; slotLabel: string;
  address?: string; vehicle?: string; extras?: string; price?: string; name?: string; phone?: string;
}
export interface ParsedMessage {
  text: string;
  buttons: string[];
  services: ServiceOption[];
  extras: ServiceOption[]; // optional add-ons the visitor can tick
  slots: string[]; // "YYYY-MM-DDTHH:mm", business time zone
  booking: BookingCard | null;
}

const BUTTON_RE = /\[(?:Boton|Botón|Opcion|Opción|Button):?\s*([^\]]+)\]/gi;
const SERVICE_RE = /\[(?:Servicio|Service):\s*([^\]]+)\]/gi;
const EXTRA_RE = /\[(?:Extra|Extras|Add-?on):\s*([^\]]+)\]/gi;
const SLOT_RE = /\[(?:Horario|Hora|Slot):\s*(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})\s*\]/gi;
const BOOKING_RE = /\[\[RESERVA:(\{[\s\S]*?\})\]\]/;

export function parseMessage(content: string): ParsedMessage {
  let booking: BookingCard | null = null;
  const bookingMatch = content.match(BOOKING_RE);
  if (bookingMatch) {
    try { booking = JSON.parse(bookingMatch[1]); } catch { booking = null; }
  }
  const buttons = [...content.matchAll(BUTTON_RE)].map(match => match[1].trim()).filter(Boolean).slice(0, 6);
  const option = (match: RegExpMatchArray) => {
    const [name, price = '', detail = ''] = match[1].split('|').map(part => part.trim());
    return { name, price, detail };
  };
  const services = [...content.matchAll(SERVICE_RE)].map(option).filter(service => service.name).slice(0, 6);
  const extras = [...content.matchAll(EXTRA_RE)].map(option).filter(extra => extra.name).slice(0, 6);
  const slots = [...new Set([...content.matchAll(SLOT_RE)].map(match => match[1]))].slice(0, 12);
  const text = content
    .replace(BOOKING_RE, '')
    .replace(/\[\[[^\]]*\]\]/g, '')
    .replace(BUTTON_RE, '')
    .replace(SERVICE_RE, '')
    .replace(EXTRA_RE, '')
    .replace(SLOT_RE, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { text, buttons, services, extras, slots, booking };
}

const escapeHtml = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

function inline(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
    // "[Choose a plan](https://…)" and bare https://… links (a checkout link, a booking page…)
    .replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+[^\s<.,;:!?)])/g, (_match, label: string | undefined, url: string | undefined, bare: string | undefined) =>
      `<a href="${url || bare}" target="_blank" rel="noopener noreferrer">${label || bare}</a>`);
}

/** Escaped HTML with bold, links, bullet lists and paragraphs. */
export function renderRichText(text: string): string {
  const blocks: string[] = [];
  let list: string[] = [];
  const flushList = () => { if (list.length) { blocks.push(`<ul>${list.map(item => `<li>${item}</li>`).join('')}</ul>`); list = []; } };
  for (const line of text.split('\n')) {
    const bullet = line.match(/^\s*(?:[-•*]|\d+[.)])\s+(.*)$/);
    if (bullet) { list.push(inline(bullet[1])); continue; }
    flushList();
    blocks.push(line.trim() ? `<p>${inline(line)}</p>` : '');
  }
  flushList();
  return blocks.filter(Boolean).join('');
}

// --- Slot labels (slots are already in the business time zone: no conversion needed) ---

const dayDate = (slot: string) => new Date(Date.UTC(Number(slot.slice(0, 4)), Number(slot.slice(5, 7)) - 1, Number(slot.slice(8, 10)), 12));

export function todayIn(timeZone?: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    return parts.slice(0, 10);
  } catch { return new Date().toISOString().slice(0, 10); }
}

const addDay = (date: string) => new Date(Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)) + 1)).toISOString().slice(0, 10);

export function dayLabel(slot: string, lang: Lang, today: string, words: { today: string; tomorrow: string }): string {
  const date = slot.slice(0, 10);
  const formatted = dayDate(slot).toLocaleDateString(lang === 'en' ? 'en-US' : 'es-PE', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
  if (date === today) return `${words.today} · ${formatted}`;
  if (date === addDay(today)) return `${words.tomorrow} · ${formatted}`;
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export function timeLabel(slot: string, lang: Lang): string {
  const hours = Number(slot.slice(11, 13));
  const minutes = slot.slice(14, 16);
  if (lang === 'en') return `${hours % 12 || 12}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
  return `${hours % 12 || 12}:${minutes} ${hours < 12 ? 'a. m.' : 'p. m.'}`;
}

export function fullSlotLabel(slot: string, lang: Lang): string {
  const day = dayDate(slot).toLocaleDateString(lang === 'en' ? 'en-US' : 'es-PE', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' });
  return lang === 'en' ? `${day} at ${timeLabel(slot, lang)}` : `el ${day} a las ${timeLabel(slot, lang)}`;
}

export function groupSlots(slots: string[]): { date: string; slots: string[] }[] {
  const groups = new Map<string, string[]>();
  for (const slot of [...slots].sort()) {
    const date = slot.slice(0, 10);
    groups.set(date, [...(groups.get(date) || []), slot]);
  }
  return [...groups.entries()].map(([date, items]) => ({ date, slots: items }));
}

/** Google Calendar link for a confirmed booking (local time + the business time zone). */
export function calendarLink(card: BookingCard, businessName: string, timeZone: string | undefined, durationMinutes = 120): string {
  const start = card.slot.replace(/[-:]/g, '') + '00';
  const endDate = new Date(Date.UTC(Number(card.slot.slice(0, 4)), Number(card.slot.slice(5, 7)) - 1, Number(card.slot.slice(8, 10)), Number(card.slot.slice(11, 13)), Number(card.slot.slice(14, 16)) + durationMinutes));
  const end = endDate.toISOString().slice(0, 16).replace(/[-:]/g, '') + '00';
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${card.service} · ${businessName}`,
    dates: `${start}/${end}`,
    details: `${card.code}${card.address ? `\n${card.address}` : ''}`,
    ...(card.address ? { location: card.address } : {}),
    ...(timeZone ? { ctz: timeZone } : {})
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export { escapeHtml };
